// Facebook Pixel utility functions
import ReactPixel from 'react-facebook-pixel';

let PIXEL_ID = null;

let isPixelBlocked = false;

const isFacebookPixelLoaded = () => {
  return typeof window.fbq !== 'undefined';
};

export const showPixelBlockedGuidance = () => {
  if (isPixelBlocked) {
    console.log(
      '%c Facebook Pixel is blocked ',
      'background: #f8f9fa; color: #000; padding: 2px 4px; border-radius: 2px; font-weight: bold;',
      '\nIt appears that your browser or an extension is blocking the Facebook Pixel tracking script. ' +
      'This is normal and won\'t affect your browsing experience. ' +
      'If you want to enable tracking for a better personalized experience, you can:' +
      '\n1. Disable your ad blocker for this site' +
      '\n2. Allow third-party cookies in your browser settings' +
      '\n3. Add this site to your ad blocker\'s whitelist'
    );
    return true;
  }
  return false;
};

export const initFacebookPixel = (pixelId) => {
  if (!pixelId) {
    console.warn('No Facebook Pixel ID provided — tracking disabled.');
    return;
  }

  PIXEL_ID = pixelId;

  try {
    const options = { autoConfig: true, debug: false };
    ReactPixel.init(PIXEL_ID, undefined, options);

    window.addEventListener('error', (event) => {
      if (event.target && event.target.src && event.target.src.includes('connect.facebook.net/en_US/fbevents.js')) {
        console.log('Facebook Pixel script is blocked by an ad blocker');
        isPixelBlocked = true;
      }
    }, true);

    setTimeout(() => {
      if (!isFacebookPixelLoaded()) {
        console.log('Facebook Pixel script is not loaded. It might be blocked by an ad blocker.');
        isPixelBlocked = true;
        showPixelBlockedGuidance();
      }
    }, 2000);
  } catch (error) {
    console.log('Error initializing Facebook Pixel:', error);
    isPixelBlocked = true;
    showPixelBlockedGuidance();
  }
};

let lastPageViewTime = 0;
let lastPageViewPath = '';
const PAGE_VIEW_THROTTLE_MS = 2000;

export const trackPageView = () => {
  if (isPixelBlocked) {
    console.log('Facebook Pixel is blocked. Page view not tracked.');
    return;
  }

  const currentPath = window.location.pathname;
  const currentTime = Date.now();

  // Don't track the same page twice in quick succession
  if (currentPath === lastPageViewPath && currentTime - lastPageViewTime < PAGE_VIEW_THROTTLE_MS) {
    console.log('Skipping duplicate page view tracking for:', currentPath);
    return;
  }

  try {
    lastPageViewTime = currentTime;
    lastPageViewPath = currentPath;

    console.log('Tracking page view for path:', currentPath);
    ReactPixel.pageView();
  } catch (error) {
    console.log('Error tracking page view:', error);
    isPixelBlocked = true;
  }
};

export const trackViewContent = (product) => {
  if (isPixelBlocked) {
    console.log('Facebook Pixel is blocked. ViewContent not tracked.');
    return;
  }
  try {
    ReactPixel.track('ViewContent', {
      content_name: product.name,
      content_category: product.category || '',
      content_ids: [product.id],
      content_type: 'product',
      value: product.price,
      currency: 'IQD'
    });
  } catch (error) {
    console.log('Error tracking ViewContent:', error);
    isPixelBlocked = true;
  }
};

export const trackAddToCart = (product, quantity = 1) => {
  if (isPixelBlocked) {
    console.log('Facebook Pixel is blocked. AddToCart not tracked.');
    return;
  }
  try {
    ReactPixel.track('AddToCart', {
      content_name: product.name,
      content_ids: [product.id],
      content_type: 'product',
      value: product.price * quantity,
      currency: 'IQD',
      contents: [
        {
          id: product.id,
          quantity,
          price: product.price
        }
      ],
      num_items: quantity
    });
  } catch (error) {
    console.log('Error tracking AddToCart:', error);
    isPixelBlocked = true;
  }
};

export const trackInitiateCheckout = (cart) => {
  if (isPixelBlocked) {
    console.log('Facebook Pixel is blocked. InitiateCheckout not tracked.');
    return;
  }
  try {
    console.log('InitiateCheckout cart items:', cart);

    const contentIds = cart.map(item => {
      const id = item.id || item.item_id || '';
      return id ? id.toString() : '';
    }).filter(id => id !== '');

    const value = cart.reduce((total, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 1;
      return total + (price * quantity);
    }, 0);

    const numItems = cart.reduce((total, item) => {
      const quantity = parseInt(item.quantity) || 1;
      return total + quantity;
    }, 0);

    console.log('Tracking InitiateCheckout with:', {
      content_ids: contentIds,
      value,
      num_items: numItems
    });

    ReactPixel.track('InitiateCheckout', {
      content_ids: contentIds,
      content_type: 'product',
      value,
      currency: 'IQD',
      num_items: numItems
    });
  } catch (error) {
    console.log('Error tracking InitiateCheckout:', error);
    isPixelBlocked = true;
  }
};

export const trackPurchase = (order) => {
  if (isPixelBlocked) {
    console.log('Facebook Pixel is blocked. Purchase not tracked.');
    return;
  }
  try {
    ReactPixel.track('Purchase', {
      value: order.total,
      currency: 'IQD',
      content_type: 'product',
      content_ids: order.items.map(item => item.id),
      num_items: order.items.reduce((total, item) => total + item.quantity, 0),
      order_id: order.id
    });
  } catch (error) {
    console.log('Error tracking Purchase:', error);
    isPixelBlocked = true;
  }
};

export default {
  initFacebookPixel,
  trackPageView,
  trackViewContent,
  trackAddToCart,
  trackInitiateCheckout,
  trackPurchase,
  showPixelBlockedGuidance
};
